import { useQuery } from '@apollo/client';
import { FileText, History, Ticket } from 'lucide-react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { EntityDetailsHeader, type EntityDetailsHeaderActions } from '@/components/ui/entity-details-header';
import { useDelayedLoading } from '@/hooks/useDelayedLoading';
import { PeriodDisplay } from '@/components/ui/period-display';
import { StatusBadge } from '@/components/ui/status-badge';
import { Tabs, TabsContent, TabsList, TabLink } from '@/components/ui/tabs';
import { AuditTab } from '@/components/ui/audit-tab';
import { EntityDetailField } from '@/components/ui/entity-detail-field';
import { VoucherDiscountTypeEnum } from '@/graphql/generated';

import {
	DetailField,
	DetailFieldsGrid,
	DetailSection
} from '@/components/ui/details-panel.tsx';
import { VOUCHER_QUERY } from './vouchers.gql.tsx';

interface VoucherDetailsProps {
	voucherId: string;
	activeTab?: string;
	onTabChange?: (tab: string) => void;
	hideTabsList?: boolean;
	// Header props for modal display
	showHeader?: boolean;
	onEdit?: (id: string) => void;
	onClone?: (id: string) => void;
	onDelete?: (id: string) => void;
	onOpenInNewWindow?: (id: string) => void;
	onClose?: () => void;
	onBack?: () => void;
	showBackButton?: boolean;
}

interface VoucherRecord {
	voucherId: string;
	campaignId?: string | null;
	campaignName?: string | null;
	name?: string | null;
	code: string;
	isAdHoc: boolean;
	isCustomerSpecific: boolean;
	customerId?: string | null;
	customerName?: string | null;
	customerEmail?: string | null;
	rateBindingRequired: boolean;
	rateId?: string | null;
	voucherDiscountTypeEnum: VoucherDiscountTypeEnum;
	voucherDiscountTypeName?: string | null;
	discountValue: number;
	maxDiscountAmount?: number | null;
	freeTimeMinutes?: number | null;
	usageLimit?: number | null;
	currentUsageCount: number;
	isActive: boolean;
	validUntilDate: string;
}

interface VoucherQueryResponse {
	voucher?: {
		voucherId: string;
		campaignId?: string | null;
		name?: string | null;
		code: string;
		isAdHoc: boolean;
		isCustomerSpecific: boolean;
		customerId?: string | null;
		rateBindingRequired: boolean;
		rateId?: string | null;
		voucherDiscountTypeEnum: VoucherDiscountTypeEnum;
		discountValue: number;
		maxDiscountAmount?: number | null;
		freeTimeMinutes?: number | null;
		usageLimit?: number | null;
		currentUsageCount: number;
		isActive: boolean;
		validUntilDate: string;
		campaign?: { campaignId: string; name?: string | null } | null;
		customer?: {
			customerId: string;
			firmName?: string | null;
			email?: string | null;
		} | null;
		voucherDiscountType?: {
			voucherDiscountTypeId: number;
			name?: string | null;
		} | null;
	} | null;
}

const formatCurrency = (value?: number | null) => {
	if (value == null) {
		return null;
	}

	return new Intl.NumberFormat(undefined, {
		style: 'currency',
		currency: 'EUR'
	}).format(value);
};

const formatDateTime = (value?: string | null) => {
	if (!value) {
		return null;
	}

	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return value;
	}

	return date.toLocaleString();
};

export const VoucherEntityIcon = () => (
	<div className="flex items-center justify-center w-14 h-14 rounded-full bg-transparent border-2 border-gray-400 dark:border-gray-500 flex-shrink-0">
		<Ticket className="w-7 h-7 text-gray-600 dark:text-gray-300" />
	</div>
);

export const VoucherDetailsHeader = ({
	voucher,
	className,
	...actions
}: {
	voucher: Pick<
		VoucherRecord,
		'voucherId' | 'isActive' | 'name' | 'code'
	>;
	className?: string;
} & EntityDetailsHeaderActions) => {
	const { t: tV } = useTranslation('vouchers');
	const entityTypeName = voucher.isActive
		? tV('details.activeVoucher')
		: tV('details.inactiveVoucher');
	const displayName = voucher.name || voucher.code;

	return (
		<EntityDetailsHeader
			icon={<VoucherEntityIcon />}
			name={displayName}
			statusBadge={<StatusBadge isActive={voucher.isActive} />}
			entityTypeName={entityTypeName}
			entityId={actions.entityId ?? voucher.voucherId}
			onEdit={actions.onEdit}
			onClone={actions.onClone}
			onDelete={actions.onDelete}
			onOpenInNewWindow={actions.onOpenInNewWindow}
			onClose={actions.onClose}
			onBack={actions.onBack}
			showBackButton={actions.showBackButton}
			showCloseButton={actions.showCloseButton ?? Boolean(actions.onClose)}
			className={className}
		/>
	);
};

export const VoucherDetails = ({
	voucherId,
	activeTab,
	onTabChange,
	hideTabsList,
	showHeader,
	onEdit,
	onClone,
	onDelete,
	onOpenInNewWindow,
	onClose,
	onBack,
	showBackButton
}: VoucherDetailsProps) => {
	const { t } = useTranslation('common');
	const { t: tV } = useTranslation('vouchers');
	const { data, loading } = useQuery<VoucherQueryResponse>(VOUCHER_QUERY, {
		variables: { voucherId }
	});

	const { shouldShowLoading } = useDelayedLoading(loading, {
		delay: 150,
		showInitialLoading: true
	});

	const voucher = useMemo<VoucherRecord | null>(() => {
		const record = data?.voucher;
		if (!record) {
			return null;
		}

		return {
			voucherId: record.voucherId,
			campaignId: record.campaignId ?? null,
			campaignName: record.campaign?.name ?? null,
			name: record.name ?? null,
			code: record.code,
			isAdHoc: record.isAdHoc,
			isCustomerSpecific: record.isCustomerSpecific,
			customerId: record.customerId ?? null,
			customerName: record.customer?.firmName ?? null,
			customerEmail: record.customer?.email ?? null,
			rateBindingRequired: record.rateBindingRequired,
			rateId: record.rateId ?? null,
			voucherDiscountTypeEnum: record.voucherDiscountTypeEnum,
			voucherDiscountTypeName: record.voucherDiscountType?.name ?? null,
			discountValue: record.discountValue,
			maxDiscountAmount: record.maxDiscountAmount ?? null,
			freeTimeMinutes: record.freeTimeMinutes ?? null,
			usageLimit: record.usageLimit ?? null,
			currentUsageCount: record.currentUsageCount,
			isActive: record.isActive,
			validUntilDate: record.validUntilDate
		};
	}, [data?.voucher]);

	if (shouldShowLoading) {
		return (
			<div className="space-y-4">
				<div className="w-24 h-5 rounded animate-pulse bg-muted" />
				<div className="w-32 h-5 rounded animate-pulse bg-muted" />
				<div className="w-full h-5 rounded animate-pulse bg-muted" />
			</div>
		);
	}

	if (!voucher) {
		return (
			<div className="py-16 text-base text-center text-muted-foreground">
				{tV('details.unavailable')}
			</div>
		);
	}

	return (
		<div className="flex flex-col h-full">
			{/* Header - shown when in modal mode */}
			{showHeader && (
				<VoucherDetailsHeader
					voucher={voucher}
					entityId={voucherId}
					onEdit={onEdit}
					onClone={onClone}
					onDelete={onDelete}
					onOpenInNewWindow={onOpenInNewWindow}
					onClose={onClose}
					onBack={onBack}
					showBackButton={showBackButton}
					showCloseButton={!!onClose}
					className="pb-4 mb-4"
				/>
			)}

		<Tabs
			defaultValue={activeTab !== undefined ? undefined : 'details'}
			value={activeTab}
			onValueChange={onTabChange}
			className="w-full flex-1"
		>
			{!hideTabsList && (
				<TabsList>
					<TabLink value="details" icon={FileText} label={t('tabs.details')} />
					<TabLink value="audit" icon={History} label={t('tabs.audit')} align="right" />
				</TabsList>
			)}

			<TabsContent value="details" className="space-y-6 mt-0 pt-0">
				<DetailSection title={t('sections.marketing')} noBorderTop>
					{voucher.isCustomerSpecific && (voucher.customerName || voucher.customerEmail) ? (
						<DetailField
							label={tV('details.customer')}
							value={voucher.customerName || voucher.customerEmail || voucher.customerId}
						/>
					) : (
						<EntityDetailField
							label={tV('details.campaign')}
							value={
								voucher.campaignName ??
								(voucher.isAdHoc
									? tV('details.adHocVoucher')
									: tV('details.noCampaign'))
							}
							entityId={voucher.campaignId ?? undefined}
							entityType="campaigns"
						/>
					)}
				</DetailSection>

				<DetailSection title={t('sections.discount_configuration')}>
					<DetailFieldsGrid>
						<DetailField
							label={tV('details.discountType')}
							value={
								voucher.voucherDiscountTypeName ??
								voucher.voucherDiscountTypeEnum
							}
						/>

						{voucher.voucherDiscountTypeEnum === VoucherDiscountTypeEnum.FreeTime ? (
							<DetailField
								label={tV('details.freeTimeMinutes')}
								value={
									voucher.freeTimeMinutes != null
										? <PeriodDisplay value={voucher.freeTimeMinutes} />
										: null
								}
							/>
						) : (
							<>
								<DetailField
									label={tV('details.discountValue')}
									value={formatCurrency(voucher.discountValue)}
								/>
								<DetailField
									label={tV('details.maxDiscount')}
									value={formatCurrency(voucher.maxDiscountAmount)}
								/>
							</>
						)}

						<DetailField
							label={tV('details.validUntil')}
							value={formatDateTime(voucher.validUntilDate)}
						/>
					</DetailFieldsGrid>
				</DetailSection>

				<DetailSection title={t('sections.usage')}>
					<DetailField
						label={tV('details.usage')}
						value={
							voucher.usageLimit != null
								? `${voucher.currentUsageCount} / ${voucher.usageLimit}`
								: `${voucher.currentUsageCount} (${tV('details.unlimited')})`
						}
					/>
				</DetailSection>

				<DetailSection title={t('sections.binding_rules')}>
					<DetailFieldsGrid>
						<DetailField
							label={tV('details.customerSpecific')}
							value={
								voucher.isCustomerSpecific
									? tV('details.yes')
									: tV('details.no')
							}
						/>
						<DetailField
							label={tV('details.customerId')}
							value={voucher.customerId ?? tV('details.anyCustomer')}
						/>
						<DetailField
							label={tV('details.rateBinding')}
							value={
								voucher.rateBindingRequired
									? tV('details.required')
									: tV('details.notRequired')
							}
						/>
						<DetailField
							label={tV('details.rateId')}
							value={voucher.rateId}
						/>
					</DetailFieldsGrid>
				</DetailSection>
			</TabsContent>

			<TabsContent value="audit" className="mt-6">
				<AuditTab data={{}} />
			</TabsContent>
		</Tabs>
		</div>
	);
};
